import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, Loader2, Save, Undo2, ImageOff } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ASSETS = [
  ["favicon_url", "Favicon", "Shown in Google results & browser tabs. Square PNG, 96×96 or larger (Google needs at least 48×48). Published on the next deploy."],
  ["apple_touch_icon_url", "Apple touch icon", "iOS home-screen icon. 180×180 PNG."],
  ["icon_192_url", "PWA icon 192", "Android / install icon. 192×192 PNG."],
  ["icon_512_url", "PWA icon 512", "Large install icon. 512×512 PNG."],
  ["site_logo_url", "Website logo", "Shown in header / branding."],
  ["social_share_image_url", "Social share image", "Default Open Graph preview. 1200×630."],
];

export default function BrandingTab() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)).catch(() => setS(false)); }, []);
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));

  const upload = async (field, file) => {
    if (!file) return;
    setBusy(field);
    const fd = new FormData();
    fd.append("file", file); fd.append("kind", "image");
    try {
      const r = await fetch(`${BACKEND}/api/admin/upload`, {
        method: "POST", body: fd, headers: { Authorization: `Bearer ${localStorage.getItem("acp_token")}` },
      });
      if (!r.ok) throw new Error();
      const data = await r.json();
      set(field, `${BACKEND}${data.url}`);
      toast.success("Uploaded — remember to Save");
    } catch { toast.error("Upload failed"); }
    finally { setBusy(""); }
  };

  const save = async () => {
    setSaving(true);
    const payload = {};
    ASSETS.forEach(([k]) => (payload[k] = s[k] || ""));
    payload.favicon_version = (s.favicon_version || 1) + 1;
    payload.favicon_prev = s.favicon_prev || "";
    try {
      const r = await api.put("/admin/settings", payload);
      setS(r.data);
      toast.success("Branding saved & published");
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const rollbackFavicon = async () => {
    if (!s.favicon_prev) { toast.info("No previous favicon to restore."); return; }
    set("favicon_url", s.favicon_prev);
    toast.info("Previous favicon restored — press Save to publish.");
  };

  if (s === false) return <p className="text-sm text-destructive">Could not load branding settings.</p>;
  if (!s) return <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</p>;

  return (
    <div className="max-w-3xl space-y-5" data-testid="branding-tab">
      <p className="text-sm text-muted-foreground">Upload your icons and branding images. Favicon references update automatically with cache-busting, so browsers pick up the new icon.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        {ASSETS.map(([field, label, hint]) => (
          <div key={field} className="rounded-xl border bg-white p-4" data-testid={`branding-${field}`}>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-lg border bg-slate-50 grid place-items-center overflow-hidden shrink-0">
                {s[field] ? <img src={s[field]} alt={label} className="w-full h-full object-contain" /> : <ImageOff size={18} className="text-slate-300" />}
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-steel text-sm">{label}</div>
                <div className="text-xs text-muted-foreground">{hint}</div>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-steel border rounded-md px-2.5 py-1.5 cursor-pointer hover:bg-slate-50">
                {busy === field ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload
                <input type="file" accept="image/*,.ico" className="hidden" onChange={(e) => upload(field, e.target.files?.[0])} data-testid={`upload-${field}`} />
              </label>
              {s[field] && <button onClick={() => set(field, "")} className="text-xs text-red-600 hover:underline" data-testid={`clear-${field}`}>Remove</button>}
              {field === "favicon_url" && <button onClick={rollbackFavicon} className="text-xs text-steel hover:underline inline-flex items-center gap-1" data-testid="favicon-rollback"><Undo2 size={12} /> Previous</button>}
            </div>
          </div>
        ))}
      </div>
      <Button className="bg-accent text-steel hover:bg-amber-600 font-semibold" onClick={save} disabled={saving} data-testid="branding-save">
        {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />} Save & publish
      </Button>
    </div>
  );
}
